import { useQuery } from '@tanstack/react-query'
import { Button, SpinnerWithBackground, Text } from '@viapeople/components'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { trpc } from '../../../utils/trpc'
import { type GroupEvaluationStepNode, RaterGroupModal } from './RaterGroupModal'

interface CycleTemplateStepProps {
	selectedTemplateId: string
	onChange: (templateId: string) => void
}

type EditingGroup = {
	id: string
	name: string
	autoPopulateFromParticipants: boolean
	required: boolean
} | null

export function CycleTemplateStep({ selectedTemplateId, onChange }: CycleTemplateStepProps) {
	const { organizationId } = useParams<{ organizationId: string }>()
	const [modalOpen, setModalOpen] = useState(false)
	const [editing, setEditing] = useState<EditingGroup>(null)

	// Fetch existing cycles marked as templates
	const { data: cyclesData, isLoading } = useQuery({
		...trpc.admin.cycles.search.queryOptions({
			organizationId: organizationId as string,
			isTemplate: true,
			perPage: 100,
		}),
		enabled: !!organizationId,
	})

	const { data: groupsData, isLoading: groupsLoading } = useQuery({
		...trpc.admin.cycles.reviewerGroups.search.queryOptions({
			cycleId: selectedTemplateId,
			search: '',
			page: 1,
			perPage: 50,
			sortBy: 'name',
			order: 'asc',
		}),
		enabled: !!selectedTemplateId,
	})

	const { data: diagramData } = useQuery({
		...trpc.admin.cycles.steps.diagram.show.queryOptions({
			organizationId: organizationId as string,
			cycleId: selectedTemplateId,
		}),
		enabled: !!selectedTemplateId && !!organizationId,
	})

	const stepNodes: GroupEvaluationStepNode[] = useMemo(() => {
		const response = diagramData as { data?: { nodes?: Array<Record<string, unknown>> } } | undefined
		const nodes = response?.data?.nodes ?? []
		return nodes
			.filter((node) => {
				const data = node.data as { stepType?: string } | undefined
				return data?.stepType === 'group_evaluation'
			})
			.map((node) => {
				const data = (node.data as { label?: string; options?: { group?: string } }) ?? {}
				return {
					id: node.id as string,
					label: data.label || 'Group Evaluation',
					currentGroupId: data.options?.group ?? null,
				}
			})
	}, [diagramData])

	const editingStepNodeId = useMemo(() => {
		if (!editing) return null
		const match = stepNodes.find((n) => n.currentGroupId === editing.id)
		return match?.id ?? null
	}, [editing, stepNodes])

	if (isLoading) {
		return <SpinnerWithBackground backgroundClass='opacity-100' />
	}

	const cycleList = cyclesData?.data ?? []
	const groups = groupsData?.data ?? []

	const handleAddGroup = () => {
		setEditing(null)
		setModalOpen(true)
	}

	const handleEditGroup = (group: (typeof groups)[number]) => {
		setEditing({
			id: group.id,
			name: group.name,
			autoPopulateFromParticipants:
				(group as { autoPopulateFromParticipants?: boolean }).autoPopulateFromParticipants ?? false,
			required: group.required,
		})
		setModalOpen(true)
	}

	return (
		<div className='flex flex-col gap-6'>
			<div>
				<Text variant='medium' weight='semibold' className='mb-1'>
					Cycle Template
				</Text>
				<Text variant='small' className='text-neutral-600'>
					Select the cycle that will be used as a template when this integration triggers a new cycle.
				</Text>
			</div>

			{cycleList.length === 0 ? (
				<div className='p-8 border-2 border-dashed border-neutral-300 rounded-lg text-center'>
					<Text variant='medium' weight='semibold' className='text-neutral-600 mb-2'>
						No Cycles Found
					</Text>
					<Text variant='small' className='text-neutral-500 mb-4'>
						You need at least one cycle to configure this integration. Create a cycle first, then come back to complete
						the setup.
					</Text>
					<Text variant='xsmall' className='text-primary-600'>
						Navigate to Cycles to create one.
					</Text>
				</div>
			) : (
				<div className='border border-neutral-200 rounded-lg divide-y divide-neutral-200 overflow-hidden max-h-[300px] overflow-y-auto'>
					{cycleList.map((cycle) => (
						<button
							key={cycle.id}
							type='button'
							onClick={() => onChange(cycle.id)}
							className={`w-full text-left p-4 transition-all cursor-pointer ${
								selectedTemplateId === cycle.id
									? 'bg-primary-50 border-l-4 border-l-primary-600'
									: 'bg-white hover:bg-neutral-50 border-l-4 border-l-transparent'
							}`}>
							<div className='flex items-center justify-between'>
								<div className='flex-1'>
									<Text variant='small' weight='semibold'>
										{cycle.name}
									</Text>
									<Text variant='xsmall' className='text-neutral-500'>
										Status: {cycle.status} · Created {new Date(cycle.createdAt).toLocaleDateString()}
									</Text>
								</div>
								<div className='flex items-center gap-2'>
									{selectedTemplateId === cycle.id && (
										<div className='w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center'>
											<svg
												className='w-3 h-3 text-white'
												fill='none'
												viewBox='0 0 24 24'
												stroke='currentColor'
												aria-hidden='true'>
												<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M5 13l4 4L19 7' />
											</svg>
										</div>
									)}
								</div>
							</div>
						</button>
					))}
				</div>
			)}

			{selectedTemplateId && (
				<div className='p-3 bg-success-50 border border-success-200 rounded-lg'>
					<Text variant='xsmall' className='text-success-700'>
						✓ Cycle selected. When this integration triggers, a new cycle will be created from this template.
					</Text>
				</div>
			)}

			{selectedTemplateId && (
				<div className='flex flex-col gap-3 border-t border-neutral-200 pt-4'>
					<div className='flex items-center justify-between'>
						<div>
							<Text variant='small' weight='semibold'>
								360° Rater Groups
							</Text>
							<Text variant='xsmall' className='text-neutral-600'>
								Configure rater groups that auto-populate with all deal team participants when the trigger fires.
							</Text>
						</div>
						<Button onClick={handleAddGroup}>Add rater group</Button>
					</div>

					{groupsLoading ? (
						<div className='py-6'>
							<SpinnerWithBackground backgroundClass='opacity-100' />
						</div>
					) : groups.length === 0 ? (
						<div className='p-4 border-2 border-dashed border-neutral-300 rounded text-center'>
							<Text variant='xsmall' className='text-neutral-600'>
								No rater groups on this template yet. Click <strong>Add rater group</strong> to create one.
							</Text>
						</div>
					) : (
						<div className='border border-neutral-200 rounded divide-y divide-neutral-200'>
							{groups.map((group) => {
								const autoPopulate =
									(group as { autoPopulateFromParticipants?: boolean }).autoPopulateFromParticipants ?? false
								const linkedStep = stepNodes.find((n) => n.currentGroupId === group.id)
								return (
									<div key={group.id} className='flex items-center justify-between p-3'>
										<div className='flex flex-col gap-1'>
											<Text variant='small' weight='semibold'>
												{group.name}
											</Text>
											<div className='flex items-center gap-2 flex-wrap'>
												{autoPopulate ? (
													<span className='inline-block text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-700 font-semibold'>
														360° auto-populate
													</span>
												) : (
													<span className='inline-block text-xs px-2 py-0.5 rounded bg-neutral-100 text-neutral-600'>
														Manual population
													</span>
												)}
												{group.required && (
													<span className='inline-block text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700'>
														Required
													</span>
												)}
												{linkedStep ? (
													<span className='inline-block text-xs px-2 py-0.5 rounded bg-success-100 text-success-700'>
														Linked to: {linkedStep.label}
													</span>
												) : (
													<span className='inline-block text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700'>
														Not linked to any step
													</span>
												)}
											</div>
										</div>
										<Button variant='secondary' onClick={() => handleEditGroup(group)}>
											Edit
										</Button>
									</div>
								)
							})}
						</div>
					)}

					<RaterGroupModal
						isOpen={modalOpen}
						onClose={() => setModalOpen(false)}
						organizationId={organizationId as string}
						cycleId={selectedTemplateId}
						editing={editing}
						stepNodes={stepNodes}
						initialStepNodeId={editingStepNodeId}
					/>
				</div>
			)}
		</div>
	)
}
